// frontend/src/state/useKpiCascadeState.ts
import { useMemo } from 'react'
import { z } from 'zod'
import { useSyncedState } from './useSyncedState'
import { useSetMultipleSearchParams } from './useSetMultipleSearchParams'
import { usePageControls, useKpisForPage } from '~/api/hooks/useCharts'
import type { KpiMetadataItem } from '~/api/endpoints/getControls'
import { titleCase } from '~/util/titleCase'
import { compareLabel } from '~/util/compareLabel'

export interface KpiCascadeState {
  encodedKpi: string
  root: string
  driver: string | null
  valueType: string | null
  /** The active item's editorial display_driver (nullable). composeChartTitle appends
   *  "driven by X" iff this is non-null — so a NULL display_driver yields a clean,
   *  suffix-free title even though `driver` is set (e.g. Media-Driven Sales). */
  driverDisplay: string | null
  axisCount: 1 | 2 | 3
  setRoot: (root: string) => void
  setDriver: (driver: string) => void
  setValueType: (vt: string) => void
  rootOptions: Array<{ value: string; label: string }>
  driverOptions: Array<{ value: string; label: string }>
  valueTypeOptions: Array<{ value: string; label: string }>
  controlsError: boolean
  /** Completes the status surface; not surfaced as UI today — get_controls is sub-second
   * post the Part-A min/max fix, so a loading affordance would only flash each load. */
  controlsLoading: boolean
  controlsEmpty: boolean
  /** FU-31: a URL-carried kpi_root the page's settled option set does not expose (cross-page
   *  arrival), else null. useReviewPageState's prune effect deletes the cascade params on this
   *  signal so effectiveRoot re-derives the page default; the notice names the removed KPI. */
  foreignRoot: string | null
  retryControls: () => void
}

export function useKpiCascadeState(
  pageId: string,
  factTable: string,
  defaultKpi?: string,
): KpiCascadeState {
  const controls = usePageControls(factTable)
  const pageKpisQuery = useKpisForPage(pageId)

  const items: KpiMetadataItem[] = useMemo(
    () => controls.data?.kpi_metadata?.items ?? [],
    [controls.data],
  )

  // When pageKpisQuery.data is undefined (still loading) pass through unfiltered
  // to avoid cascade flicker. Once loaded, narrow to page-allowed kpi_ids only.
  const filteredItems: KpiMetadataItem[] = useMemo(() => {
    if (pageKpisQuery.data === undefined) return items
    return items.filter((m) => (pageKpisQuery.data as string[]).includes(m.kpi_id))
  }, [items, pageKpisQuery.data])

  const [root] = useSyncedState('kpi_root', z.string(), 'url', '')
  const [driver] = useSyncedState('kpi_driver', z.string(), 'url', '')
  const [valueType, setValueTypeRaw] = useSyncedState('kpi_value_type', z.string(), 'url', '')
  const setMultiple = useSetMultipleSearchParams()

  const axisCount: 1 | 2 | 3 = useMemo(() => {
    if (filteredItems.length === 0) return 1
    const hasFullAxes = filteredItems.some((m) => m.driver !== null && m.value_type !== null)
    return hasFullAxes ? 3 : 1
  }, [filteredItems])

  const rootOptions = useMemo(() => {
    if (filteredItems.length === 0) return []
    const seen = new Map<string, string>()
    for (const m of filteredItems) {
      if (!seen.has(m.kpi_root)) seen.set(m.kpi_root, m.display_root ?? m.kpi_root)
    }
    return Array.from(seen, ([value, label]) => ({ value, label })).sort((a, b) =>
      compareLabel(a.label, b.label),
    )
  }, [filteredItems])

  // Fires for both (a) the fact exposes no kpi_metadata at all, and (b) the page's
  // kpi allowlist excludes every item the fact exposes. The banner message covers
  // (a); (b) is an operator misconfiguration — tolerated for v1 (split if UX needs it).
  const controlsEmpty = controls.isSuccess && filteredItems.length === 0

  // When the user hasn't picked a root via URL, prefer the page's defaultKpi over the
  // first available root. defaultKpi can be: a full kpi_id with driver+value_type
  // (e.g. 'sales__total__units' — seeds ALL THREE axes, so a page can lead with a
  // specific slice), a bare kpi_id (e.g. 'nsp'), or a kpi_root (e.g. 'sales_cpo').
  // Falls through to the FIRST METADATA (insertion-order) root if defaultKpi resolves
  // to none of those — see `firstRoot` below. The rootOptions DISPLAY list is
  // alphabetically sorted, but the default fallback deliberately is NOT, so sorting
  // the dropdown never silently changes which KPI a page leads with.
  const seeded = useMemo(() => {
    if (!defaultKpi) return { root: '', driver: '', valueType: '' }
    const byKpiId = filteredItems.find((m) => m.kpi_id === defaultKpi)
    if (byKpiId) {
      return {
        root: byKpiId.kpi_root,
        driver: byKpiId.driver ?? '',
        valueType: byKpiId.value_type ?? '',
      }
    }
    const byKpiRoot = filteredItems.find((m) => m.kpi_root === defaultKpi)
    if (byKpiRoot) return { root: byKpiRoot.kpi_root, driver: '', valueType: '' }
    if (rootOptions.some((o) => o.value === defaultKpi)) {
      return { root: defaultKpi, driver: '', valueType: '' }
    }
    return { root: '', driver: '', valueType: '' }
  }, [defaultKpi, filteredItems, rootOptions])

  // Default fallbacks use metadata (insertion) order, NOT the sorted display order.
  const firstRoot = filteredItems[0]?.kpi_root ?? ''
  // Compute effective root first — needed to filter driver options
  const effectiveRoot = root || seeded.root || firstRoot

  // FU-31: a URL-carried root absent from this page's SETTLED options. Settled = controls
  // succeeded AND the page KPI list arrived (until then filteredItems pass through unfiltered
  // — :48-53 — and a reset would false-fire). Empty option sets never flag (controlsEmpty
  // owns that state). The URL write happens in useReviewPageState's prune effect — NEVER
  // here (single-writer rule, useReviewPageState.ts:358).
  const optionsSettled = controls.isSuccess && pageKpisQuery.data !== undefined
  const foreignRoot =
    optionsSettled &&
    root !== '' &&
    rootOptions.length > 0 &&
    !rootOptions.some((o) => o.value === root)
      ? root
      : null

  const driverOptions = useMemo(() => {
    if (axisCount < 2 || !effectiveRoot) return []
    const seen = new Map<string, string>()
    for (const m of filteredItems) {
      if (m.kpi_root === effectiveRoot && m.driver !== null && !seen.has(m.driver)) {
        seen.set(m.driver, m.display_driver ?? titleCase(m.driver))
      }
    }
    return Array.from(seen, ([value, label]) => ({ value, label })).sort((a, b) =>
      compareLabel(a.label, b.label),
    )
  }, [filteredItems, effectiveRoot, axisCount])

  const firstDriver =
    axisCount >= 2 && effectiveRoot
      ? (filteredItems.find((m) => m.kpi_root === effectiveRoot && m.driver !== null)?.driver ?? '')
      : ''
  // The seeded driver applies ONLY while the seeded root is the effective one —
  // a user-chosen root must get that root's own first-driver fallback, not the seed.
  const seededDriver = effectiveRoot === seeded.root ? seeded.driver : ''
  // Compute effective driver next — needed to filter value_type options
  const effectiveDriver = axisCount >= 2 ? driver || seededDriver || firstDriver : null

  const valueTypeOptions = useMemo(() => {
    if (axisCount < 3 || !effectiveRoot || !effectiveDriver) return []
    const seen = new Map<string, string>()
    for (const m of filteredItems) {
      if (
        m.kpi_root === effectiveRoot &&
        m.driver === effectiveDriver &&
        m.value_type !== null &&
        !seen.has(m.value_type)
      ) {
        seen.set(m.value_type, m.display_value_type ?? titleCase(m.value_type))
      }
    }
    return Array.from(seen, ([value, label]) => ({ value, label })).sort((a, b) =>
      compareLabel(a.label, b.label),
    )
  }, [filteredItems, effectiveRoot, effectiveDriver, axisCount])

  const firstValueType =
    axisCount === 3 && effectiveRoot && effectiveDriver
      ? (filteredItems.find(
          (m) =>
            m.kpi_root === effectiveRoot && m.driver === effectiveDriver && m.value_type !== null,
        )?.value_type ?? '')
      : ''
  // The seeded valueType applies only when both root AND driver match the seed —
  // switching either axis must fall back to that combo's own first value_type.
  const seededValueType =
    effectiveRoot === seeded.root && effectiveDriver === seeded.driver ? seeded.valueType : ''
  // Compute effective valueType last
  const effectiveValueType = axisCount === 3 ? valueType || seededValueType || firstValueType : null

  const driverDisplay = useMemo(() => {
    if (axisCount < 3 || !effectiveRoot || !effectiveDriver) return null
    const item = filteredItems.find(
      (m) => m.kpi_root === effectiveRoot && m.driver === effectiveDriver,
    )
    return item?.display_driver ?? null
  }, [filteredItems, effectiveRoot, effectiveDriver, axisCount])

  const encodedKpi = useMemo(() => {
    if (axisCount === 1) return effectiveRoot
    if (axisCount === 3 && effectiveDriver && effectiveValueType) {
      return `${effectiveRoot}__${effectiveDriver}__${effectiveValueType}`
    }
    return effectiveRoot
  }, [axisCount, effectiveRoot, effectiveDriver, effectiveValueType])

  return {
    encodedKpi,
    root: effectiveRoot,
    driver: effectiveDriver,
    valueType: effectiveValueType,
    driverDisplay,
    axisCount,
    setRoot: (next) => {
      // Atomically set root + clear driver + valueType in one setSearchParams call.
      // Back-to-back single-key calls race (see useSyncedState comment on stale prev).
      setMultiple({ kpi_root: next, kpi_driver: null, kpi_value_type: null })
    },
    setDriver: (next) => {
      // Atomically set driver + clear valueType.
      setMultiple({ kpi_driver: next, kpi_value_type: null })
    },
    setValueType: setValueTypeRaw,
    rootOptions,
    driverOptions,
    valueTypeOptions,
    controlsError: controls.isError,
    controlsLoading: controls.isLoading,
    controlsEmpty,
    foreignRoot,
    retryControls: () => controls.refetch(),
  }
}
